import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import KeyValue, { type KeyValueModel } from '@/components/KeyValue';
import PERMISSIONS from '@/constants/auth.constants';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import { useStyles } from '@/features/DevicesTable';
import type { LimaSourcesResponseModal } from '@/features/DevicesTable/DevicesTable.types';
import type {
  ConnectionInputSchema,
  ConnectionInputs
} from '@/features/SourceCreate';
import SOURCE_TYPE, {
  CATEGORY_SUPPORTED_SOURCE_TYPES,
  READ_ONLY_MANAGED_SOURCE_TYPES
} from '@/features/SourceCreate/SourceCreate.constants';

import { filteredLimaSourcesAtom } from '../../Devices.store';
import LimaSourceTrendChart from '../../LimaSourceTrendChart/LimaSourceTrendChart';
import SourceStatusCard from '../SourceStatusCard';
import ViewDataProviders from '../ViewDataProviders/ViewDataProviders';
import {
  SCROLL_CARD_STYLE,
  SECTION_MARGIN_STYLE,
  SOURCE_CONTENT_STYLE
} from './SourceCard.constants';
import type { SourceCardProps } from './SourceCard.types';

/**
 * Masks sensitive values while preserving the last four characters.
 *
 * @param secretValue Sensitive string value.
 * @returns Masked value.
 */
const maskSecretValue = (secretValue: string): string => {
  const lastFourDigits = secretValue.slice(-4);
  const asterisks = '*'.repeat(15);

  return `${asterisks}${lastFourDigits}`;
};

/**
 * Builds key/value rows for source metadata and connection-input details.
 *
 * @remarks
 * The function intentionally preserves field ordering and display fallbacks
 * currently used in source details.
 *
 * @param item Source details model.
 * @param lastSeenDisplay Display value resolved from list-level metadata.
 * @param lastUpdatedAtDisplay Localized update timestamp display.
 * @returns Key/value rows for rendering.
 */
const buildSourceItems = (
  item: SourceCardProps['item'],
  lastSeenDisplay: string,
  lastUpdatedAtDisplay: string
): KeyValueModel[] => {
  const sourceItems: KeyValueModel[] = [];
  const connectionInputs = item.connection_inputs as ConnectionInputs;

  const sourceTypeId = item?.source_type_id;
  if (READ_ONLY_MANAGED_SOURCE_TYPES.has(sourceTypeId)) {
    sourceItems.push({
      label: 'Account Name',
      value: connectionInputs?.account_name || '-'
    });

    sourceItems.push({
      label: 'Tenant ID',
      value: connectionInputs?.id || '-'
    });
  }

  sourceItems.push({
    label: 'Type',
    value: item?.source_type?.name?.trim().replace(/\s+/g, '') ?? ''
  });
  sourceItems.push({
    label: 'Site',
    value: item?.site_name?.trim() ? item.site_name : '-'
  });
  sourceItems.push({
    label: 'IP Address',
    value: item?.ip_address?.trim() ? item.ip_address : '-'
  });

  const schema = item?.source_type?.connection_input_schema as
    | ConnectionInputSchema
    | undefined;

  schema?.source_inputs?.forEach((field) => {
    const { name, label, type } = field;
    const value = connectionInputs[name as keyof ConnectionInputs];

    if (name === 'api_url') {
      return;
    }

    if (type === 'password' && typeof value === 'string') {
      sourceItems.push({ label, value: maskSecretValue(value) });
      return;
    }

    if (typeof value === 'string') {
      if (name === 'ingest_from') {
        sourceItems.push({
          label,
          value: value
            ? dayjs
                .utc(value as string)
                .local()
                .format(YEAR_MONTH_DAY_TIME_FORMAT)
            : '-'
        });
        return;
      }

      sourceItems.push({ label, value: value.trim() || '-' });
      return;
    }

    if (name === 'contact_email') {
      sourceItems.push({ label, value: '-' });
    }
  });

  sourceItems.push({
    label: 'State',
    value: item?.active ? 'Active' : 'Inactive'
  });
  sourceItems.push({
    label: 'Last Seen',
    value: lastSeenDisplay
  });
  sourceItems.push({
    label: 'Last Updated At',
    value: lastUpdatedAtDisplay
  });
  sourceItems.push({
    label: 'Last Updated By',
    value: item?.updated_by_user?.email || '-'
  });

  return sourceItems;
};

/**
 * Displays details for a selected cloud source.
 *
 * @param props Component properties.
 * @returns Source detail card.
 */
const SourceCard = (props: SourceCardProps) => {
  const { item } = props;
  const { classes } = useStyles();
  const [filteredLimaSources] = useAtom(filteredLimaSourcesAtom);

  // Use last_seen_display from filteredLimaSources to avoid mismatches
  // between the listing last_seen value and the display value shown in source details.

  const sourceMap = useMemo(() => {
    const map = new Map<string, LimaSourcesResponseModal>();

    filteredLimaSources.forEach((parent) => {
      map.set(parent.source_uid, parent);
      parent.data_providers?.forEach((child: LimaSourcesResponseModal) => {
        map.set(child.source_uid, child);
      });
    });

    return map;
  }, [filteredLimaSources]);

  const lastSeenDisplay =
    sourceMap.get(item?.source_uid)?.last_seen_display ?? '-';

  const lastUpdatedAtDisplay = item?.updated_at
    ? dayjs
        .utc(item?.updated_at as string)
        .local()
        .format(YEAR_MONTH_DAY_TIME_FORMAT)
    : '-';

  const sourceItems = useMemo(
    () => buildSourceItems(item, lastSeenDisplay, lastUpdatedAtDisplay),
    [item, lastSeenDisplay, lastUpdatedAtDisplay]
  );

  return (
    <CardContent style={SOURCE_CONTENT_STYLE} className={classes.cardContent}>
      {item && (
        <Box style={SECTION_MARGIN_STYLE}>
          <Box className={classes.deviceDetailCommon}>
            <CheckPermissions permissions={[PERMISSIONS.SOURCES_READ]}>
              <KeyValue items={sourceItems} />
            </CheckPermissions>
          </Box>
        </Box>
      )}

      {/* View Data Providers */}

      {item &&
        (item?.source_type_id === SOURCE_TYPE.AWS_S3 ||
          item?.source_type_id === SOURCE_TYPE.GOOGLE_STORAGE) && (
          <ViewDataProviders item={item} />
        )}

      {/* Categories */}

      {item?.connection_inputs &&
        Array.isArray(item?.connection_inputs?.category) &&
        item.connection_inputs?.category.length > 0 &&
        CATEGORY_SUPPORTED_SOURCE_TYPES.includes(item.source_type_id) && (
          <Box style={SECTION_MARGIN_STYLE}>
            <Typography variant="h6" sx={{ mt: 1.5, mb: 1.5 }}>
              CATEGORIES
            </Typography>

            <Card variant="outlined" style={SCROLL_CARD_STYLE}>
              <CardContent>
                <Stack spacing={0.5}>
                  {item?.connection_inputs?.category
                    ?.slice()
                    .sort((a: string, b: string) => a.localeCompare(b))
                    .map((catName: string) => (
                      <Typography key={catName} sx={{ mt: 1, mb: 0.5 }}>
                        {catName}
                      </Typography>
                    )) || null}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

      {/* Source Trend Chart */}
      {item?.source_type_id !== SOURCE_TYPE.MICROSOFT && (
        <Box
          sx={{
            marginBottom:
              item?.source_statuses && item.source_statuses.length <= 0
                ? '4rem!important'
                : '0rem!important'
          }}
          style={SECTION_MARGIN_STYLE}
        >
          <Divider sx={{ my: 1 }} />
          <LimaSourceTrendChart />
          <Divider sx={{ my: 1 }} />
        </Box>
      )}

      {/* Source Multiple Status Codes rendering */}
      {/* Microsoft status is excluded because it does not have its own status in the DB */}
      {item?.source_type_id !== SOURCE_TYPE.MICROSOFT &&
        item?.source_statuses &&
        item.source_statuses.length > 0 && (
          <Box style={{ margin: '1rem' }}>
            <Typography variant="h6" sx={{ mt: 1.5, mb: 1.5 }}>
              {item?.source_statuses && item.source_statuses.length > 1
                ? 'STATUSES'
                : 'STATUS'}
            </Typography>

            {/* Show source statuses */}
            <Stack spacing={1} sx={{ paddingBottom: '4rem' }}>
              <SourceStatusCard item={item} key="source-status" />
            </Stack>
          </Box>
        )}
    </CardContent>
  );
};

export default SourceCard;
